import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from '@material-ui/core/Modal';
import IconButton from '@material-ui/core/IconButton';
import Slider from '@material-ui/core/Slider';
import CloseIcon from '@material-ui/icons/Close';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';

// Full-screen image viewer overlay with zoom (up to 100% of the image's native
// resolution) and pan. Closes via the X button, the ESC key, or a click on the
// dark backdrop. Zoom is expressed as a fraction of the natural size, where
// 1 = 100% (one image pixel per screen pixel). The image opens "fit to screen"
// and can be zoomed up to 100%.
const MAX_ZOOM = 1; // 100% of natural size
const ZOOM_STEP = 0.25;

export default function ImageOverlay({ open, src, alt, onClose }) {
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });
  const [fitZoom, setFitZoom] = useState(1);
  const [zoom, setZoom] = useState(1);
  const scrollRef = useRef(null);
  const dragRef = useRef({ active: false, startX: 0, startY: 0, left: 0, top: 0 });

  // Compute the zoom level that makes the image fit fully within the viewport
  // (never upscaling past 100%). This is the minimum zoom / initial view.
  const computeFitZoom = useCallback((w, h) => {
    if (!w || !h) return 1;
    const margin = 96; // leave room for the toolbar / breathing space
    const availW = Math.max(window.innerWidth - margin, 1);
    const availH = Math.max(window.innerHeight - margin, 1);
    return Math.min(availW / w, availH / h, MAX_ZOOM);
  }, []);

  const handleImageLoad = useCallback(
    (e) => {
      const w = e.target.naturalWidth;
      const h = e.target.naturalHeight;
      setNaturalSize({ w, h });
      const fit = computeFitZoom(w, h);
      setFitZoom(fit);
      setZoom(fit);
    },
    [computeFitZoom],
  );

  const clampZoom = useCallback(
    (value) => Math.min(Math.max(value, fitZoom), MAX_ZOOM),
    [fitZoom],
  );

  const changeZoom = useCallback(
    (next) => setZoom((prev) => clampZoom(typeof next === 'function' ? next(prev) : next)),
    [clampZoom],
  );

  // Reset state whenever a new image is opened.
  useEffect(() => {
    if (open) {
      setNaturalSize({ w: 0, h: 0 });
      setFitZoom(1);
      setZoom(1);
    }
  }, [open, src]);

  // ESC to close, +/- to zoom.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === '+' || e.key === '=') {
        changeZoom((z) => z + ZOOM_STEP);
      } else if (e.key === '-' || e.key === '_') {
        changeZoom((z) => z - ZOOM_STEP);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose, changeZoom]);

  // Ctrl/pinch or plain wheel zoom, centered on the pointer.
  const handleWheel = (e) => {
    e.preventDefault();
    changeZoom((z) => z + (e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP));
  };

  // Drag to pan when the image overflows the viewport.
  const handleMouseDown = (e) => {
    const el = scrollRef.current;
    if (!el) return;
    dragRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      left: el.scrollLeft,
      top: el.scrollTop,
    };
  };
  const handleMouseMove = (e) => {
    const el = scrollRef.current;
    if (!el || !dragRef.current.active) return;
    el.scrollLeft = dragRef.current.left - (e.clientX - dragRef.current.startX);
    el.scrollTop = dragRef.current.top - (e.clientY - dragRef.current.startY);
  };
  const endDrag = () => {
    dragRef.current.active = false;
  };

  const displayWidth = naturalSize.w ? naturalSize.w * zoom : undefined;
  const zoomPct = Math.round(zoom * 100);
  const canPan = zoom > fitZoom;

  return (
    <Modal open={open} onClose={onClose} aria-label={alt || 'image'}>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.9)',
          outline: 'none',
        }}
      >
        {/* Toolbar */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 8,
            padding: '8px 12px',
            background:
              'linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 100%)',
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <IconButton
            size="small"
            style={{ color: '#fff' }}
            onClick={() => changeZoom((z) => z - ZOOM_STEP)}
            disabled={zoom <= fitZoom}
            aria-label="Zoom out"
          >
            <ZoomOutIcon />
          </IconButton>
          <Slider
            value={zoom}
            min={fitZoom}
            max={MAX_ZOOM}
            step={0.01}
            onChange={(_, v) => changeZoom(Array.isArray(v) ? v[0] : v)}
            style={{ width: 160, color: '#fff' }}
            aria-label="Zoom"
          />
          <IconButton
            size="small"
            style={{ color: '#fff' }}
            onClick={() => changeZoom((z) => z + ZOOM_STEP)}
            disabled={zoom >= MAX_ZOOM}
            aria-label="Zoom in"
          >
            <ZoomInIcon />
          </IconButton>
          <span
            style={{
              color: '#fff',
              minWidth: 48,
              textAlign: 'center',
              fontSize: 14,
              userSelect: 'none',
            }}
          >
            {zoomPct}%
          </span>
          <IconButton
            style={{ color: '#fff' }}
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </div>

        {/* Scrollable image stage. Clicking the empty backdrop closes. */}
        <div
          ref={scrollRef}
          onClick={onClose}
          onWheel={handleWheel}
          style={{
            position: 'absolute',
            inset: 0,
            overflow: 'auto',
            display: 'flex',
            alignItems: canPan ? 'flex-start' : 'center',
            justifyContent: canPan ? 'flex-start' : 'center',
          }}
        >
          {src && (
            <img
              src={src}
              alt={alt || 'image'}
              onLoad={handleImageLoad}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => {
                if (canPan) {
                  e.preventDefault();
                  handleMouseDown(e);
                }
              }}
              onMouseMove={handleMouseMove}
              onMouseUp={endDrag}
              onMouseLeave={endDrag}
              draggable={false}
              style={{
                width: displayWidth ? `${displayWidth}px` : 'auto',
                maxWidth: displayWidth ? 'none' : '100%',
                maxHeight: displayWidth ? 'none' : '100%',
                margin: 'auto',
                cursor: canPan ? 'grab' : 'default',
                userSelect: 'none',
              }}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

ImageOverlay.propTypes = {
  open: PropTypes.bool,
  src: PropTypes.string,
  alt: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};
